/**
 * API functions for the Claim Portal.
 */
import { apiClient } from "./lib/apiClient";
import { env } from "./lib/env";
import axios from "axios";
import {
  mockStores,
  unknownStoreResponse,
  mockSelfOnboardResponse,
  delay,
} from "./mocks";
import type {
  StoreValidationResponse,
  SelfOnboardRequest,
  SelfOnboardResponse,
  StoreInfo,
} from "./types";

function axiosDetailMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const d = err.response?.data?.detail as unknown;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d) && d.length) {
    const parts = d
      .map((x: { msg?: string; message?: string; loc?: string[] }) => x.msg || x.message)
      .filter(Boolean);
    if (parts.length) return parts.join(" ");
  }
  return null;
}

/** Catalogue roles slice for Claim form (bundled CSV on server). */
export type RolesByStoreResponse = {
  store_id: string;
  group: string;
  concept: string;
  location: string;
  roles: string[];
};

export async function fetchRolesByStore(storeId: string): Promise<RolesByStoreResponse> {
  if (env.useMocks) {
    await delay(200);
    return {
      store_id: storeId.trim(),
      group: "Team Member",
      concept: "Camp Margaritaville",
      location: "Demo",
      roles: ["Server", "Busser", "Bartender", "Host", "Assistant General Manager"],
    };
  }

  try {
    const response = await apiClient.get<RolesByStoreResponse>("/catalog/margaritaville/roles-by-store", {
      params: { store_id: storeId.trim() },
    });
    return response.data;
  } catch (err) {
    const detail = axiosDetailMessage(err);
    throw new Error(detail || (err instanceof Error ? err.message : "Failed to load job titles."));
  }
}

export async function listSelfRegistrationStores(): Promise<StoreInfo[]> {
  if (env.useMocks) {
    // Fallback mock: derive a unique list from mockStores
    await delay(300);
    return Object.values(mockStores)
      .map((v) => v.store)
      .filter((s): s is StoreInfo => Boolean(s));
  }

  try {
    const response = await apiClient.get<StoreInfo[]>("/stores/self-registration");
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (!err.response) {
        throw new Error("Unable to reach the server. Please try again in a moment.");
      }
      if (err.response.status === 404) {
        throw new Error("Locations are not available right now. Please contact your administrator.");
      }
      if (err.response.status >= 500) {
        throw new Error("We couldn’t load locations due to a server error. Please try again later.");
      }
    }
    throw err instanceof Error ? err : new Error("Failed to load locations.");
  }
}

/**
 * Look up a store by store_id to validate it exists and is active.
 * Used in step 1 of the claim flow.
 */
export async function lookupStore(storeId: string): Promise<StoreValidationResponse> {
  if (env.useMocks) {
    await delay(800);
    const normalizedId = storeId.trim();
    return mockStores[normalizedId] ?? unknownStoreResponse;
  }

  const response = await apiClient.get<StoreValidationResponse>(
    `/stores/lookup/${encodeURIComponent(storeId.trim())}`
  );
  return response.data;
}

/**
 * Submit self-onboarding request to create a Workvivo account.
 * Used in step 2 of the claim flow after store validation.
 */
export async function submitSelfOnboard(
  request: SelfOnboardRequest
): Promise<SelfOnboardResponse> {
  if (env.useMocks) {
    await delay(1500);
    const storeData = mockStores[request.store_id];
    const storeName = storeData?.store?.business_name ?? "Unknown Store";
    return mockSelfOnboardResponse(storeName);
  }

  try {
    const response = await apiClient.post<SelfOnboardResponse>("/claim/", request);
    return response.data;
  } catch (err) {
    const detail = axiosDetailMessage(err);
    throw new Error(detail || (err instanceof Error ? err.message : "Request failed."));
  }
}
